// Account's Billing box, exactly as Account.dc.html draws it: the line about Stripe, the next
// payment with Manage billing beside it, and, for a product whose cancel is off in Stripe's portal
// (the Accelerator), the line saying so. It shows only for someone with a Stripe customer of their
// own (CLAUDE.md: a guest checkout has none, so Manage billing shows only when there's one).
// Manage billing opens Stripe's billing page in a new tab, already logged in as them.
// Stage 3: a product they pay for on a payment plan gets a line too, since its cancel is off in
// Stripe's billing page as the Accelerator's is (Adam, 2 Oct; member-billing's cancel_off_plans).
// While staff view as a member, Billing is read-only: nothing is asked of Stripe and Manage
// billing doesn't open. The page's own viewing-as line at the top says so, so Billing adds no line
// of its own (Adam, 3 Oct); a refusal from the function itself shows that same line.
// A secured place's balance is a payment to come too (stage 3's review): with no subscription
// payment due, Next payment names the earliest balance and its date (Account passes `secured`),
// never "Nothing due".
// 10 Oct: nobody cancels a paid product themselves (Adam), so every paid product they hold comes back with its cancel
// off, and a line shows only for a fixed-term membership, a payment plan or a running subscription (notes.js).
// 11 Oct (member review): Manage billing can't take a balance, so when Next payment is a secured place's balance it
// says the amount Stripe gives, and the place secured page's own pay button sits beside Manage billing.
import { useEffect, useState } from 'react'
import * as f from '../lib/format.js'
import { loadBillingSummary, openBillingPortal } from '../lib/billing.js'
import { SITE } from '../config/site.js'
import { VIEWING_AS_LINE } from '../lib/viewAs.jsx'
import { billingNotes } from './notes.js'

const FAILED = `Stripe's billing page didn't open. Try again, or email ${SITE.helpEmail}.`

// "The Property Accelerator, £249+vat on 25 October"
export function nextPaymentLine(next) {
  if (!next) return 'Nothing due'
  const on = `${next.amount} on ${f.dayMonthLong(f.calendarDay(next.due_on))}`
  return next.product_name ? `${next.product_name}, ${on}` : on
}

// "Profit On Your Terms balance, £597+vat, due 20 October", without the amount when Stripe gives none and
// without a date while there's none
export function balanceLine(place) {
  const amount = place.amount ? `, ${place.amount}` : ''
  return place.dueAt ? `${place.name} balance${amount}, due ${f.dayMonthLong(place.dueAt)}` : `${place.name} balance${amount}`
}

// The board's line for the Accelerator, whose members have a group to message Adam in: "The
// Property Accelerator is a 12-month commitment, so it can't be cancelled here. If something has
// changed, message Adam." A product with no fixed term gets the help address instead (Adam, 3 Oct).
export function cancelOffLine(p) {
  if (p.term_months) return `${p.name} is a ${p.term_months}-month commitment, so it can't be cancelled here. If something has changed, message Adam.`
  return `${p.name} can't be cancelled here. If something has changed, email ${SITE.helpEmail}.`
}

// "Profit On Your Terms is on a payment plan, so it can't be cancelled here. If something has
// changed, email adam@adamslee.co.uk." Only Accelerator members have a group to message Adam in
// (Adam, 3 Oct).
export function planLine(name) {
  return `${name} is on a payment plan, so it can't be cancelled here. If something has changed, email ${SITE.helpEmail}.`
}

export default function BillingSection({ readOnly = false, secured = [] }) {
  const [summary, setSummary] = useState({ status: readOnly ? 'read_only' : 'loading' })
  const [state, setState] = useState('idle')

  useEffect(() => {
    if (readOnly) return undefined
    let live = true
    loadBillingSummary()
      .then(d => { if (live) setSummary({ status: 'ready', ...d }) })
      .catch(e => { if (live) setSummary({ status: 'error', code: e.code }) })
    return () => { live = false }
  }, [readOnly])

  function manage() {
    if (state === 'opening' || readOnly) return
    setState('opening')
    openBillingPortal()
      .then(() => setState('idle'))
      .catch(e => setState(e.code === 'viewing_as' ? 'viewing_as' : 'failed'))
  }

  // with no card payment due, Next payment is the earliest balance, with its pay button
  const balance = summary.status === 'ready' && !summary.next_payment && secured.length ? secured[0] : null
  const line = summary.status !== 'ready' ? ''
    : balance ? balanceLine(balance)
      : nextPaymentLine(summary.next_payment)
  // a line about cancelling only where there's something to cancel: a fixed-term membership, a payment plan or a
  // running subscription, never a one-off purchase (notes.js; Adam, 10 Oct)
  const { cancelOff, plans } = summary.status === 'ready' ? billingNotes(summary) : { cancelOff: [], plans: [] }

  return (
    <section id="billing" className="ac-card" aria-labelledby="billing-h">
      <h2 id="billing-h" className="ac-h2">Billing</h2>
      <p className="ac-lede">Your cards, invoices and receipts are kept by Stripe, who take the payments. Update your card or download an invoice there.</p>
      <div className="ac-billing">
        <div className="ac-billing-next">
          <span className="ac-billing-label">Next payment</span>
          <span className="ac-billing-line" aria-busy={summary.status === 'loading' || undefined}>{line}</span>
        </div>
        <div className="ac-billing-actions">
          {balance?.pay && <a className="ac-billing-btn ac-billing-pay" href={balance.pay.href}>{balance.pay.label}</a>}
          <button type="button" className={balance?.pay ? 'ac-billing-btn ac-billing-btn--line' : 'ac-billing-btn'} onClick={manage} disabled={readOnly}
            aria-busy={state === 'opening' || undefined}>Manage billing</button>
        </div>
      </div>
      {state === 'failed' && <p className="ac-msg" role="alert">{FAILED}</p>}
      {!readOnly && state === 'viewing_as' && <p className="ac-msg" role="alert">{VIEWING_AS_LINE}</p>}
      {cancelOff.map(p => <p key={p.product_id} className="ac-note">{cancelOffLine(p)}</p>)}
      {plans.map(name => <p key={name} className="ac-note">{planLine(name)}</p>)}
    </section>
  )
}
